/**
 * Calculate the lightweight NEIS byte count used by the saenggibu backend.
 * ASCII is 1 byte, line breaks are 2 bytes, and every other Unicode code point
 * is 3 bytes.
 */
export const calculateNEISBytes = (text) => {
  if (!text) return 0;

  let bytes = 0;
  const normalizedText = String(text).replace(/\r\n?/g, '\n');
  for (const character of normalizedText) {
    if (character === '\n') {
      bytes += 2;
    } else if (character.charCodeAt(0) < 128) {
      bytes += 1;
    } else {
      bytes += 3;
    }
  }
  return bytes;
};

/**
 * NEIS 항목별 공식 상한(바이트). 단일 진실원은 백엔드
 * `backend/utils/saenggibu_byte_guard.py` 의 `_SAENGGIBU_OFFICIAL_MAX_BY_CATEGORY` /
 * `get_saenggibu_official_max_bytes` 이고, 이 표는 그 미러다. 두 쪽이 어긋나면
 * 백엔드 `tests/test_saenggibu_byte_limit_single_source.py` 가 실패한다.
 * 규칙: 초등 창체·동아리만 3000B, 그 외는 표 값, 표에 없는 카테고리는 1500B.
 */
export const SAENGGIBU_OFFICIAL_MAX_BYTES = Object.freeze({
  교과: 1500,
  교과_고등: 1500,
  행동발달: 1500,
  창체: 1500,
  동아리: 1500,
  진로: 2100,
  비슷한문장: 3000,
});

export const SAENGGIBU_DEFAULT_MAX_BYTES = 1500;
export const SAENGGIBU_MIN_TARGET_BYTES = 100;

/**
 * 기존에 이미 허용하던 목표 바이트 상한(옛 단일 슬라이더: 행동발달·동아리 2100B).
 * 상한은 내리지 않고 공식 상한이 더 큰 경우만 올린다. 백엔드
 * `_SAENGGIBU_LEGACY_TARGET_MAX_BY_CATEGORY` 미러.
 */
export const SAENGGIBU_LEGACY_TARGET_MAX_BYTES = Object.freeze({
  행동발달: 2100,
  동아리: 2100,
});

/** NEIS 공식 상한 (바이트 가이드 표시용). 백엔드 get_saenggibu_official_max_bytes 미러 */
export const getSaenggibuOfficialMaxBytes = (category, schoolLevel = '중등') => {
  const name = String(category || '').trim();
  const level = String(schoolLevel || '중등').trim();
  if ((name === '창체' || name === '동아리') && level === '초등') return 3000;
  return SAENGGIBU_OFFICIAL_MAX_BYTES[name] ?? SAENGGIBU_DEFAULT_MAX_BYTES;
};

/**
 * 목표 바이트 슬라이더/요청 상한 = max(공식 상한, 기존 허용 상한).
 * 백엔드 get_saenggibu_target_max_bytes 미러.
 */
export const getSaenggibuMaxBytes = (category, schoolLevel = '중등') => {
  const name = String(category || '').trim();
  return Math.max(
    getSaenggibuOfficialMaxBytes(name, schoolLevel),
    SAENGGIBU_LEGACY_TARGET_MAX_BYTES[name] ?? 0,
  );
};

/** 목표 바이트를 [100, 공식 상한] 으로 고정 (백엔드 clamp_saenggibu_target_bytes 와 동일) */
export const clampSaenggibuTargetBytes = (wordCount, category, schoolLevel = '중등') => {
  const requested = Number(wordCount) || 0;
  if (requested <= 0) return requested;
  return Math.max(SAENGGIBU_MIN_TARGET_BYTES, Math.min(requested, getSaenggibuMaxBytes(category, schoolLevel)));
};

export default calculateNEISBytes;
